import {
	Box,
	Button,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Text,
	VStack
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";

// Types
import { ClaimStatus, UserRole } from "../../utils/consts";
import { KVP } from "../../utils/types";

// Hooks
import useClaimsToast from "../../hooks/useToast";

// API
import { getAnalystList, postBulkUpdateClaims } from "../../utils/api/claims";
import { ClaimPerson, ClaimsStatus, GetAnalystsResponse } from "../../utils/api/protos/messages/claims.message_pb";

// Utils
import { getClaimStatusFromBffStatus } from "../../utils/helpers";

interface BulkUpdateClaimsModalProps {
	userRoles: UserRole[];
	isOpen: boolean;
	onClose: () => void;
	selectedClaims: Array<KVP>;
	onSuccess: () => void;
}

const BulkUpdateClaimsModal: React.FC<BulkUpdateClaimsModalProps> = ({
	userRoles,
	isOpen,
	onClose,
	selectedClaims,
	onSuccess
}) => {
	const [assigneeId, setAssigneeId] = useState<string>("");
	const [statusId, setStatusId] = useState<string>("");
	const [dueDate, setDueDate] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [analysts, setAnalysts] = useState<ClaimPerson[]>([]);
	const [loadingAnalysts, setLoadingAnalysts] = useState<boolean>(false);

	// Check if user is Claims Manager or Payments
	const isClaimsManager = userRoles.includes(UserRole.ClaimsManager);
	const isPayments = userRoles.includes(UserRole.Payments);

	const toast = useClaimsToast();
	const heldClaims = selectedClaims.filter((claim) => claim.isHeldForAudit === 1);

	// Load analysts when updateAssignee is checked for the first time
	useEffect(() => {
		if (analysts.length === 0 && !loadingAnalysts) {
			setLoadingAnalysts(true);
			getAnalystList()
				.then((response: GetAnalystsResponse) => {
					setAnalysts(response.getAnalystsList());
				})
				.catch((err) => {
					console.error("Error loading analysts:", err);
					toast("Error", "Failed to load analysts", "error");
				})
				.finally(() => {
					setLoadingAnalysts(false);
				});
		}
	}, [analysts.length, loadingAnalysts, toast]);

	// Reset form when modal closes and set default due date to current month
	useEffect(() => {
		if (!isOpen) {
			setAssigneeId("");
			setDueDate("");
			setStatusId("");
			setIsLoading(false);
		}
	}, [isOpen]);

	const handleReset = () => {
		setDueDate("");
		setAssigneeId("");
		setStatusId("");
	};

	const handleSubmit = async () => {
		setIsLoading(true);

		try {
			const claimIds = selectedClaims.map((claim) => Number(claim.id));
			const selectedAssigneeId = assigneeId ? Number(assigneeId) : undefined;
			const selectedStatusId = statusId ? Number(statusId) : undefined;
			const selectedDueDate = dueDate ? new Date(dueDate) : undefined;

			if (selectedStatusId && heldClaims.length > 0) {
				toast(
					"Error",
					`Status cannot be changed for held audit claim${heldClaims.length > 1 ? "s" : ""}: ${heldClaims
						.map((claim) => claim.id)
						.join(", ")}`,
					"error"
				);
				return;
			}

			const statusUpdates = await postBulkUpdateClaims(claimIds, selectedStatusId, selectedAssigneeId, selectedDueDate);

			let successNumber = claimIds.length;
			let failedNumber = 0;
			const updatedFields = [];
			if (statusId && statusUpdates != null) {
				successNumber = statusUpdates.filter((status) => status.hasStatus()).length;
				failedNumber = statusUpdates.filter((status) => !status.hasStatus()).length;
				const statusLabel = getClaimStatusFromBffStatus(selectedStatusId as ClaimsStatus);
				updatedFields.push(`status to ${statusLabel.toString()}`);
			}
			if (assigneeId) {
				const selectedAnalyst = analysts.find((analyst) => analyst.getId() === Number(assigneeId));
				updatedFields.push(`assignee to ${selectedAnalyst?.getName() || "Unknown"}`);
			}
			if (dueDate) {
				// Parse as local midnight so the displayed date matches the selected date
				// (`new Date("YYYY-MM-DD")` parses as UTC and shifts back a day in US timezones)
				updatedFields.push(`due date to ${new Date(`${dueDate}T00:00:00`).toLocaleDateString()}`);
			}

			if (successNumber > 0) {
				toast(
					"Success",
					`Successfully updated ${updatedFields.join(" and ")} for ${successNumber} claim${successNumber > 1 ? "s" : ""}`,
					"success"
				);
			}
			if (failedNumber > 0) {
				toast(
					"Warning",
					`Could not update ${updatedFields.join(" and ")} for ${failedNumber} claim${failedNumber > 1 ? "s" : ""}`,
					"warning"
				);
			}

			onSuccess();
			onClose();
		} catch (err) {
			console.error("Error in bulk update:", err);
			const errorMessage = err instanceof Error ? err.message : String(err);
			toast("Error", errorMessage, "error");
		} finally {
			setIsLoading(false);
		}
	};

	const getTodayDate = (): string => {
		return new Date().toISOString().split("T")[0];
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Bulk Update Claims</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<VStack spacing={4} align="stretch">
						<Box>
							<Text fontSize="sm" color="gray.600" mb={2}>
								Selected Claims: {selectedClaims.length}
							</Text>
							<Text fontSize="sm" color="gray.600">
								Claim IDs: {selectedClaims.map((claim) => claim.id).join(", ")}
							</Text>
							{heldClaims.length > 0 && (
								<Text fontSize="sm" color="orange.600" mt={2}>
									Held for audit: {heldClaims.map((claim) => claim.id).join(", ")}
								</Text>
							)}
						</Box>

						{isClaimsManager && (
							<>
								<Box>
									<Text fontSize="sm" fontWeight={500} mb={1}>
										Assignee
									</Text>
									<Select
										placeholder="Select new assignee"
										value={assigneeId}
										onChange={(e) => setAssigneeId(e.target.value)}
										isDisabled={loadingAnalysts}>
										{loadingAnalysts ? (
											<option>Loading analysts...</option>
										) : (
											analysts.map((analyst) => (
												<option key={analyst.getId()} value={analyst.getId()}>
													{analyst.getName()}
												</option>
											))
										)}
									</Select>
								</Box>
								<Box>
									<Text fontSize="sm" fontWeight={500} mb={1}>
										Due Date
									</Text>
									<Input
										type="date"
										placeholder="Select Date and Time"
										value={dueDate}
										onChange={(e) => setDueDate(e.target.value)}
										min={getTodayDate()}
									/>
								</Box>
							</>
						)}
						{isPayments && (
							<Box>
								<Text fontSize="sm" fontWeight={500} mb={1}>
									Claim Status
								</Text>
								<Select placeholder="Select new status" value={statusId} onChange={(e) => setStatusId(e.target.value)}>
									<option key={ClaimsStatus.CLOSED_PAID.toString()} value={ClaimsStatus.CLOSED_PAID.toString()}>
										{ClaimStatus.ClosedPaid}
									</option>
								</Select>
							</Box>
						)}
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button variant="ghost" mr={3} onClick={onClose} isDisabled={isLoading}>
						Cancel
					</Button>
					<Button variant="ghost" mr={3} onClick={handleReset} isDisabled={isLoading}>
						Reset
					</Button>
					<Button
						colorScheme="orange"
						onClick={handleSubmit}
						isLoading={isLoading}
						loadingText="Updating..."
						isDisabled={!assigneeId && !dueDate && !statusId}>
						Update
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default BulkUpdateClaimsModal;
